import ModeBadge from '@/components/console/mode-badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { WiringProvider } from '@/types/platform';
import { Check, Minus, X } from 'lucide-react';

// One provider on the checklist: what it powers and which keys it still needs. Names only.
const ProviderCard = ({ provider }: { provider: WiringProvider }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <h3 className="text-base">{provider.name}</h3>
          <ModeBadge mode={provider.mode} />
        </CardTitle>
        <CardDescription>{provider.role}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 text-sm">
        <div>
          <h4 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Required
          </h4>
          <ul className="mt-1.5 space-y-1">
            {provider.required.map((name) =>
              provider.missing.includes(name) ? (
                <EnvName key={name} name={name} state="missing" />
              ) : (
                <EnvName key={name} name={name} state="set" />
              )
            )}
          </ul>
        </div>

        {provider.optional.length > 0 ? (
          <div>
            <h4 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              Optional
            </h4>
            <ul className="mt-1.5 space-y-1">
              {provider.optional.map((name) =>
                provider.setOptional.includes(name) ? (
                  <EnvName key={name} name={name} state="set" />
                ) : (
                  <EnvName key={name} name={name} state="unset" />
                )
              )}
            </ul>
          </div>
        ) : null}

        {provider.gate ? (
          <div>
            <h4 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              A person still has to do
            </h4>
            <p className="mt-1.5">{provider.gate}</p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const EnvName = ({ name, state }: { name: string; state: 'set' | 'missing' | 'unset' }) => {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {state === 'set' ? (
        <Check className="h-4 w-4 shrink-0 text-green-700" aria-hidden="true" />
      ) : null}
      {state === 'missing' ? (
        <X className="h-4 w-4 shrink-0 text-red-700" aria-hidden="true" />
      ) : null}
      {state === 'unset' ? (
        <Minus className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
      ) : null}
      <code className="text-xs break-all">{name}</code>
      {state === 'set' ? <span className="text-xs text-green-700">set</span> : null}
      {state === 'missing' ? <span className="text-xs text-red-700">missing</span> : null}
      {state === 'unset' ? <span className="text-muted-foreground text-xs">not set</span> : null}
    </li>
  );
};

export default ProviderCard;
